// EDEN · the ONE week rule (owner ruling 2026-10-07, F-2026-09-28-02): app Week N is the school
// calendar's WN. A term's weeks are its school days taken five at a time from its first school day
// (curriculum/_build/calendar-2026-27.json, week_convention five_school_days_running). A week runs
// from its first school day until the day before the next week's first school day, so weekends and
// holidays are absorbed into the week they follow and every date belongs to exactly one week; its
// deadline (functions/api/_lib/time.ts) is 23:59 on that last day.
//
// School days come from the calendar file between its first and last school day; outside that span
// (a year no file covers yet) Monday–Friday stand in, which keeps a Monday-start term's weeks
// Monday–Sunday exactly as before. The server (functions/api/_lib/time.ts) and the client
// (src/calendar-view.ts) both import this module — neither keeps a copy of its own.
//
// Pure: no DOM, no fetch, no clock. Dates are YYYY-MM-DD (Cambodia-local), UTC-noon anchored.

import { school_days as ay2026_27 } from "../curriculum/_build/calendar-2026-27.json";

export interface TermSpan {
  start_date: string;
  end_date: string;
}
export interface WeekRange {
  start: string;
  end: string;
}

const DAY_MS = 86_400_000;
function msOf(iso: string): number {
  const [y, m, d] = iso.split("-").map(Number) as [number, number, number];
  return Date.UTC(y, m - 1, d, 12);
}
const isoOf = (ms: number): string => new Date(ms).toISOString().slice(0, 10);
const addDays = (iso: string, n: number): string => isoOf(msOf(iso) + n * DAY_MS);
const daysBetween = (from: string, to: string): number => Math.round((msOf(to) - msOf(from)) / DAY_MS);

// One entry per academic-year calendar file — add next year's `school_days` here once it is built.
const YEARS = [ay2026_27].map((days) => ({ first: days[0]!, last: days[days.length - 1]!, days: new Set(days) }));

/** Is `iso` a school day? The calendar file's answer inside the years it covers; Mon–Fri outside. */
export function isSchoolDay(iso: string): boolean {
  const year = YEARS.find((y) => iso >= y.first && iso <= y.last);
  if (year) return year.days.has(iso);
  const dow = new Date(msOf(iso)).getUTCDay();
  return dow !== 0 && dow !== 6;
}

const cache = new Map<string, WeekRange[]>();
/** The term's own weeks, in order: five school days each from its first school day (a short final
 *  week keeps what is left), each running to the day before the next one begins. The last runs to
 *  the day before the next school day after the term — the next term's first day, as a rule. */
function weeksOf(term: TermSpan): WeekRange[] {
  const key = `${term.start_date}|${term.end_date}`;
  const known = cache.get(key);
  if (known) return known;
  const starts: string[] = [];
  let counted = 0;
  for (let d = term.start_date; d <= term.end_date; d = addDays(d, 1)) {
    if (!isSchoolDay(d)) continue;
    if (counted % 5 === 0) starts.push(d);
    counted++;
  }
  let next = addDays(term.end_date, 1);
  while (!isSchoolDay(next)) next = addDays(next, 1);
  const weeks = starts.map((start, i) => ({ start, end: addDays(starts[i + 1] ?? next, -1) }));
  cache.set(key, weeks);
  return weeks;
}

/** The dates week `weekNo` of the term runs. Outside the term's own weeks the count carries on in
 *  7-day blocks (before the first, after the last), so every whole number names a range and weekOf()
 *  is its exact inverse. */
export function termWeekRange(term: TermSpan, weekNo: number): WeekRange {
  const weeks = weeksOf(term);
  const own = weeks[weekNo - 1];
  if (own) return own;
  const last = weeks[weeks.length - 1];
  const [anchor, anchorNo] =
    weeks.length === 0 ? [term.start_date, 1] : weekNo < 1 ? [weeks[0]!.start, 1] : [addDays(last!.end, 1), weeks.length + 1];
  const start = addDays(anchor, (weekNo - anchorNo) * 7);
  return { start, end: addDays(start, 6) };
}

/** Which week of the term `date` falls in — unclamped: 0 or less before the term's first school day,
 *  more than the term's own weeks after its last. */
export function weekOf(date: string, term: TermSpan): number {
  const weeks = weeksOf(term);
  if (weeks.length === 0) return Math.floor(daysBetween(term.start_date, date) / 7) + 1;
  if (date < weeks[0]!.start) return Math.floor(daysBetween(weeks[0]!.start, date) / 7) + 1;
  const i = weeks.findIndex((w) => date <= w.end);
  if (i >= 0) return i + 1;
  return weeks.length + 1 + Math.floor(daysBetween(addDays(weeks[weeks.length - 1]!.end, 1), date) / 7);
}
